import React, { useId, useState } from 'react';

/** A single-series time trend: thin line, faint area fill, recessive grid,
 * hover crosshair + tooltip, and the last point direct-labeled so the
 * headline number reads without hovering. One series needs no legend —
 * the panel title above it already names what's being measured. */
export default function TrendChart({ points, color = '#6739e0', valueSuffix = '', emptyLabel, height = 180 }) {
  const gradientId = useId();
  const [hoverIdx, setHoverIdx] = useState(null);

  if (!points || points.length === 0) {
    return <p className="text-sm text-gray-500">{emptyLabel}</p>;
  }

  const width = 600;
  const padTop = 16;
  const padBottom = 24;
  const padLeft = 8;
  const padRight = 8;
  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;

  const values = points.map((p) => p.value || 0);
  const maxV = Math.max(...values, 1);
  const minV = Math.min(...values, 0);
  const range = maxV - minV || 1;

  const xAt = (i) => padLeft + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const yAt = (v) => padTop + plotH - ((v - minV) / range) * plotH;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.value || 0)}`).join(' ');
  const areaPath = `${linePath} L ${xAt(points.length - 1)} ${padTop + plotH} L ${xAt(0)} ${padTop + plotH} Z`;

  const gridLines = [0, 0.5, 1].map((f) => padTop + plotH * f);
  const lastIdx = points.length - 1;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridLines.map((y, i) => (
          <line key={i} x1={padLeft} y1={y} x2={width - padRight} y2={y} stroke="#eef0f4" strokeWidth="1" />
        ))}

        <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {hoverIdx !== null && (
          <line
            x1={xAt(hoverIdx)} y1={padTop} x2={xAt(hoverIdx)} y2={padTop + plotH}
            stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity="0.4"
          />
        )}

        {points.map((p, i) => (
          <circle
            key={i}
            cx={xAt(i)}
            cy={yAt(p.value || 0)}
            r={i === lastIdx || hoverIdx === i ? 4 : 0}
            fill="white"
            stroke={color}
            strokeWidth="2"
          />
        ))}

        {points.map((p, i) => (
          <rect
            key={`hit-${i}`}
            x={xAt(i) - plotW / points.length / 2}
            y={0}
            width={plotW / points.length}
            height={height}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
          />
        ))}

        <text x={xAt(lastIdx)} y={yAt(points[lastIdx].value || 0) - 10} textAnchor="end" fontSize="12" fontWeight="700" fill={color}>
          {points[lastIdx].value}{valueSuffix}
        </text>

        {points.map((p, i) => (
          (i === 0 || i === lastIdx || i === Math.floor(lastIdx / 2)) && (
            <text key={`lbl-${i}`} x={xAt(i)} y={height - 4} textAnchor={i === 0 ? 'start' : i === lastIdx ? 'end' : 'middle'} fontSize="10" fill="#9ca3af">
              {p.label}
            </text>
          )
        ))}
      </svg>

      {hoverIdx !== null && (
        <div
          className="absolute -translate-x-1/2 bg-gray-900 text-white text-xs font-medium px-2 py-1 rounded whitespace-nowrap pointer-events-none"
          style={{
            left: `${(xAt(hoverIdx) / width) * 100}%`,
            top: `${Math.max(0, (yAt(points[hoverIdx].value || 0) / height) * 100 - 14)}%`,
          }}
        >
          {points[hoverIdx].label}: {points[hoverIdx].value}{valueSuffix}
        </div>
      )}
    </div>
  );
}
